import { useState } from 'react';
import {
  Box,
  Table,
  TableCell,
  TableRow,
  Button,
  Grid,
  TableBody,
} from '@mui/material';

import { Helmet } from 'react-helmet-async';

import Plan from '../../../../models/Plan/Plan';
import api from '../../../../services/api';
import ModalComponent from '../../../../components/ModalComponent';
import ChangedPlan from './ChangedPlan';

import { useUserContext } from '../../../../contexts/UserContext';

function PlanConfirm({
  plan,
  oldPlanName,
  setSelectedPlan,
  requestPlansList,
}: {
  plan: Plan,
  oldPlanName: string,
  setSelectedPlan: (plan: Plan) => void,
  requestPlansList: (callback: (isLoading: boolean) => void) => Promise<void>
}) {
  // variables
  const [openModal, setOpenModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [changedPlan, setChangedPlan] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const { updatePlanInfo } = useUserContext();

  // functions

  const handleShowModal = () => {
    setOpenModal(!openModal);
  };

  const handleCancel = () => {
    setSelectedPlan(null);
  };

  const handleConfirm = async () => {
    setIsLoading(true);
    await api.post('v1/plans/change-plan', {
      id: plan.default_price,
      name: plan.name,
      old_plan_name: oldPlanName,
      lang: navigator.language,
    })
      .then((res) => {
        const { changedSuccessfully, error } = res.data;
        if (error) {
          setErrorMessage(error);
          return;
        }

        if (changedSuccessfully) {
          handleShowModal();
          setChangedPlan(true);
          updatePlanInfo({
            name: plan.name,
            initial: plan.metadata?.plan_initial,
            feat_pages: plan.metadata?.feat_pages,
          });
        }
      }).finally(() => {
        setIsLoading(false);
      });
  };

  // render conditions
  if (changedPlan) {
    return (
      <ChangedPlan
        setSelectedPlan={setSelectedPlan}
        setChangedSuccessfully={setChangedPlan}
        requestPlansList={requestPlansList}
        selectedPlanName={plan.name}
      />
    );
  }

  return (
    <>
      <Helmet>
        <title>ご契約プランの変更</title>
      </Helmet>
      <Box className="container-fluid py-5">
        <Box className="text-bold text-center">
          <h2>ご契約プランの変更</h2>
          <p>ご契約のプランを変更いたします。プラン情報をご確認ください。</p>
        </Box>
        <Box className="card mt-4">
          <Box className="card-body">
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ backgroundColor: '#f5f5f5' }}>プラン名</TableCell>
                  <TableCell>{plan.name}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ backgroundColor: '#f5f5f5' }}>ご利用開始日</TableCell>
                  <TableCell>{plan.expirationDate}</TableCell>
                </TableRow>
              </TableBody>
            </Table>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                marginTop: '1rem',
              }}
            >
              <Box className="border p-3">
                <h4 className="text-bold">ご契約プラン変更の注意事項</h4>
                <p>
                  ・ご契約プラン変更の取り消しはできません。
                  <br />
                  ・ご契約プランの変更を行うとは元のプランに戻すことはできません。
                  <br />
                  ・作成したページは、変更後も継続してご利用いただけます。
                  <br />
                  ・有料プランは翌月より料金が発生いたします。
                </p>
              </Box>
              <Grid
                gap={2}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginTop: '1rem',
                }}
              >
                <Button variant="outlined" onClick={handleCancel}>
                  戻る
                </Button>
                <Button variant="contained" onClick={handleShowModal}>
                  プラン変更のお申込みをする
                </Button>
              </Grid>
            </Box>
          </Box>
        </Box>
      </Box>

      <ModalComponent
        title="ご契約プランの変更"
        confirmButtonText="ご契約プラン変更"
        cancelButtonText="閉じる"
        onConfirm={handleConfirm}
        closeModal={handleShowModal}
        show={openModal}
        loading={isLoading}
        errorMessage={errorMessage}
      >
        ご契約プランを変更してもよろしいですか？
      </ModalComponent>
    </>
  );
}

export default PlanConfirm;
